import React, { Dispatch, useEffect, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Checkbox, Input } from '@node-space/ui-components'
import { contactAtom } from 'pages/Contacts/atoms/contactAtoms'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressTypes, FormFieldLabels } from 'pages/Contacts/types/fiatInstruments'
import {
  BeneficiaryDetails,
  BeneficiaryDetailsCurrency,
  BeneficiaryEntityType,
  PaymentMethods,
} from 'types/beneficiaries'
import { ContactEntity } from 'types/contacts'
import { AccountHolderDetails } from './AccountHolderDetails'
import { AddressDetails } from './AddressDetails'

type BankAccountBeneficiaryDetailsProps = {
  formValues: ContactFiatInstrumentFormData
  errors: FieldErrors<ContactFiatInstrumentFormData>
  isBusinessAccount: boolean
  isInternationalBankAccount: boolean
  formFieldLabels: FormFieldLabels
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setError: UseFormSetError<ContactFiatInstrumentFormData>
  setIsContactSheetOpen: Dispatch<React.SetStateAction<boolean>>
  contactBeneficiary?: BeneficiaryDetails
}
export const BankAccountBeneficiaryDetails = ({
  formValues,
  errors,
  isInternationalBankAccount,
  clearErrors,
  setValue,
  register,
  setError,
}: BankAccountBeneficiaryDetailsProps) => {
  const { t } = useTranslation()

  const contact = useAtomValue(contactAtom)

  const isInternational = formValues?.paymentMethods?.includes(PaymentMethods.SWIFT)
  const isLocal = !isInternational && !!formValues?.paymentMethods?.length
  const isCurrencySelected = !!formValues?.currency
  const isPaymentMethodSelected = !!formValues?.paymentMethods?.length

  const isLocalUsd = useMemo(() => {
    return formValues?.currency === BeneficiaryDetailsCurrency.USD && isLocal
  }, [formValues?.currency, isLocal])

  useEffect(() => {
    if (contact?.entity?.type) {
      const entityType =
        contact.entity.type === ContactEntity.COMPANY
          ? BeneficiaryEntityType.BUSINESS
          : BeneficiaryEntityType.INDIVIDUAL
      setValue('entityType', entityType)
    }
  }, [contact?.entity?.type])

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      })
    }

  const handleSameAsContactAddressChange = (isSame: boolean) => {
    setValue('sameAsContactAddress', isSame)
    setValue('beneficiaryAddressLine1', isSame ? contact?.entity?.address?.addressLine1 : '')
    setValue('beneficiaryAddressLine2', isSame ? contact?.entity?.address?.addressLine2 : '')
    setValue('beneficiaryCity', isSame ? contact?.entity?.address?.city : '')
    setValue('beneficiaryRegion', isSame ? contact?.entity?.address?.region : '')
    setValue('beneficiaryPostCode', isSame ? contact?.entity?.address?.postCode : '')
    setValue('beneficiaryCountryCode', isSame ? contact?.entity?.address?.country : '')
    setValue('beneficiaryCountryName', isSame ? contact?.entity?.address?.country : '')
  }

  const isContentVisible = useMemo(() => {
    return isCurrencySelected && isPaymentMethodSelected
  }, [isCurrencySelected, isPaymentMethodSelected, isLocalUsd])

  const formattedContactAddress = useMemo(() => {
    const { addressLine1, addressLine2, city, region, postCode, country } =
      contact?.entity?.address ?? {}
    return [addressLine1, addressLine2, city, region, postCode, country].filter(Boolean).join(', ')
  }, [contact?.entity?.address])

  return (
    <Box width="full">
      <Box
        flexCol
        width="full"
        gapY={16}
        paddingT={24}
        paddingB={isInternationalBankAccount ? null : 12}
      >
        <Box gapY={16} flexCol width="full">
          {isContentVisible && (
            <Box gapY={16} flexCol width="full">
              <AccountHolderDetails
                formValues={formValues}
                errors={errors}
                register={register}
                setValue={setValue}
              />
              <Input
                {...register('description')}
                testId="description"
                label={t('formFields.beneficiaryDescription')}
                placeholder={t('formFields.placeholder.beneficiaryDescription')}
                errorText={errors?.description?.message}
                error={!!errors?.description?.message}
                value={formValues?.description}
                onChange={handleInputChange('description')}
              />

              <Box flexCol width="full" paddingT={16} paddingB={8} gapY={16}>
                <h2 className="text-foreground-prominent text-h2">
                  {t('formFields.beneficiaryAddress')}
                </h2>

                <Checkbox
                  id="beneficiary-address-copy-details-checkbox"
                  name="beneficiary-address-copy-details-checkbox"
                  testId="beneficiary-address-copy-details-checkbox"
                  onCheckedChange={handleSameAsContactAddressChange}
                  label={t('contacts.sameAddressAsContact')}
                  checked={formValues?.sameAsContactAddress}
                >
                  {formattedContactAddress}
                </Checkbox>
              </Box>

              <AddressDetails
                key={formValues?.sameAsContactAddress?.toString()}
                type={AddressTypes.BENEFICIARY}
                errors={errors}
                formValues={formValues}
                clearErrors={clearErrors}
                register={register}
                setError={setError}
                setValue={setValue}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
